import { Container } from "aurelia-dependency-injection";

import { GoogleMapsService } from "../services/google-maps.service";
import config from "../common/config";
import { ILocation } from "../resources-vue/vue-interfaces/i-location";
import { IGeoCodeResponse } from "../resources-vue/vue-interfaces/i-map";
import eventBus from "../../src/utilities/eventBus";
import { useApp } from "../pages-vue/App/useApp";

export enum GeoLocationEvents {
    Retrieved = "app:location:success"
}

export class GeolocationService {
    public _googleMapsService: GoogleMapsService;
    public _locationKey: string = config.locationCacheKey;
    public locationData: ILocation = {};
    public isRequestingLocation: boolean = false;
    private appStore: any;

    // TODO: Add computedFrom for this getter
    public get hasGeo() {
        return !$.isEmptyObject(this.locationData);
    }

    public constructor() {
        this._googleMapsService = Container.instance.get(GoogleMapsService);
        this.appStore = useApp();
        this.hydrateFromCache();
    }

    // Seeds the singleton from a previously entered/retrieved location, so pages
    // that mount before the location is re-resolved this session (e.g. TasksMap,
    // PatientVisitMap, EmployeeVisitMap) can use it immediately instead of prompting again.
    private hydrateFromCache(): void {
        const cached: string = localStorage.getItem(this._locationKey);
        if (cached) {
            try {
                Object.assign(this.locationData, JSON.parse(cached));
            } catch (error) {
                console.error(error?.message);
            }
        }
    }

    public async getCoordinatesByZip(zip: string): Promise<void> {
        await this._googleMapsService.getCoordsFromAddress(zip, (result: IGeoCodeResponse) => {
            this.updateLocationData({ ...result, postal_code: zip }, false);
        });
    }

    public async getAddressFromCoords(lat: string, lng: string): Promise<void> {
        await this._googleMapsService.getAddressFromCoords(
            {
                lat: parseFloat(lat),
                lng: parseFloat(lng)
            },
            (result: IGeoCodeResponse) => {
                this.updateLocationData({ ...result, lat, lng }, true);
            }
        );
    }

    private updateLocationData(result: IGeoCodeResponse, fromGeo: boolean) {
        if (result) {
            Object.assign(this.locationData, {
                lat: result.lat,
                lon: result.lng,
                zip: result.postal_code,
                city: result.city,
                state: result.state,
                willingToShareLocation: true,
                fromGeo
            });
            this.cacheLocation();
            this.announceLocationRetrieval();
        } else if (result.hasError) {
            console.log(result.message);
        }
    }

    public cacheLocation() {
        localStorage.setItem(this._locationKey, JSON.stringify(this.locationData));
        this.appStore.setCookie();
    }

    public announceLocationRetrieval(locationData?: ILocation) {
        if (!!locationData) {
            Object.assign(this.locationData, locationData);
        }
        console.log(`GEOLOCATION: Publishing location success.`, this.locationData);
        eventBus.emit(GeoLocationEvents.Retrieved, this.locationData);
    }
}

// Copies the retrieved location into `locationData` and returns false when none is available yet,
// so callers can skip map init instead of centering on NaN coordinates.
// Typed structurally (not as GeolocationService) so Vue's reactivity unwrapping of `this._geolocationService`
// doesn't fail nominal typing against the class's private members.
export function ensureLocationData(
    geolocationService: { hasGeo: boolean; locationData: ILocation },
    locationData: ILocation
): boolean {
    if (!geolocationService.hasGeo) {
        return false;
    }
    Object.assign(locationData, geolocationService.locationData);
    return true;
}

export function hasFiniteCoords(lat: number, lon: number): boolean {
    return Number.isFinite(lat) && Number.isFinite(lon);
}

// Prompts the browser for the device's current position (same flow WeatherReport used to duplicate) and
// resolves it to an address via the GeolocationService. Successful lookups fire GeoLocationEvents.Retrieved;
// `onUnavailable` covers unsupported/blocked/denied, and `onResolved` lets callers distinguish a cache hit
// from a fresh geocode lookup (e.g. for analytics) without duplicating the getCurrentPosition wiring.
export function requestDeviceLocation(
    geolocationService: {
        hasGeo: boolean;
        isRequestingLocation: boolean;
        locationData: ILocation;
        _locationKey: string;
        getAddressFromCoords: (lat: string, lng: string) => Promise<void>;
        announceLocationRetrieval: (locationData?: ILocation) => void;
    },
    onUnavailable: (reason: "unsupported" | "denied") => void,
    onResolved?: (source: "cache" | "geocode") => void
): void {
    if (geolocationService.hasGeo || geolocationService.isRequestingLocation) {
        return;
    }
    if (!("geolocation" in navigator)) {
        onUnavailable("unsupported");
        return;
    }
    geolocationService.isRequestingLocation = true;
    navigator.geolocation.getCurrentPosition(
        async (position: GeolocationPosition) => {
            geolocationService.isRequestingLocation = false;
            const lat: string = position.coords.latitude.toFixed(3);
            const lon: string = position.coords.longitude.toFixed(3);
            const cacheString: string = localStorage.getItem(geolocationService._locationKey);
            const cache = cacheString && JSON.parse(cacheString);

            if (cache && cache.lat === lat && cache.lon === lon) {
                Object.assign(geolocationService.locationData, cache);
                geolocationService.announceLocationRetrieval(geolocationService.locationData);
                onResolved?.("cache");
            } else {
                await geolocationService.getAddressFromCoords(lat, lon);
                onResolved?.("geocode");
            }
        },
        () => {
            geolocationService.isRequestingLocation = false;
            onUnavailable("denied");
        }
    );
}
